import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { GridStack } from "gridstack";

import type { GridItemHTMLElement, GridStackWidget } from "gridstack";
import FooterText from "src/components/footerText";
import AuthSubHeader from "src/components/authSubHeader";
import ActionDashBoardFilter from "src/components/manufacturer/filters/ActionDashBoardFilter";
import CoverageAvailable from "src/pages/actionDashboard/coverageAvailable";
import ActionRequired from "src/pages/actionDashboard/actionRequired";
import SpecialityPharmacy from "src/pages/actionDashboard/specialityPharmacy";
import PendingTable from "src/pages/actionDashboard/pending";
import useFetch from "src/hooks/useFetch";
import { AuthContext } from "src/context/authContext";
import { LoaderContext } from "src/context/loaderContext";
import { getRole } from "src/helpers";
import { initialState, tableFilterReducer } from "./reducer";
import { APIEndpoints } from "src/types/apiTypes";
import { TodoResponseType } from "src/pages/actionDashboard/types";
import { UserObjectType } from "src/components/authSubHeader/types";
import {
  ActionStatisticsDashboardActionType,
  PatientFilterStateActions,
  PatientManufacturerFilterStart,
} from "./types";

/* ============================================================
 * DEFAULT SECTION ORDER
 * ============================================================
 */

const DEFAULT_SECTION_ORDER = [
  "coverage_available",
  "action_required",
  "specialty_pharmacy",
  "pending",
] as const;

type DashboardSectionId = (typeof DEFAULT_SECTION_ORDER)[number];

/* ============================================================
 * TYPES
 * ============================================================
 */

interface DashboardSectionPosition {
  id: DashboardSectionId;
  position: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface GridSectionProps {
  id: DashboardSectionId;

  title: ReactNode;

  children: ReactNode;

  isCollapsed: boolean;

  onToggleCollapse: () => void;
}

/* ============================================================
 * HELPERS
 * ============================================================
 */

/**
 * Checks whether value is one of our dashboard section IDs.
 */
const isDashboardSectionId = (value: unknown): value is DashboardSectionId =>
  typeof value === "string" &&
  DEFAULT_SECTION_ORDER.includes(value as DashboardSectionId);

/**
 * Use API section order only when all 4 unique sections exist.
 */
const resolveSectionOrder = (order?: string[] | null): DashboardSectionId[] => {
  const validOrder = order?.filter(isDashboardSectionId) ?? [];

  return validOrder.length === DEFAULT_SECTION_ORDER.length &&
    new Set(validOrder).size === DEFAULT_SECTION_ORDER.length
    ? validOrder
    : [...DEFAULT_SECTION_ORDER];
};

/**
 * Check that GridStack is still mounted.
 */
const isGridActive = (grid: GridStack | null): grid is GridStack =>
  Boolean(grid?.el?.isConnected);

/**
 * Returns dashboard sections in current visual order.
 */
const getSectionPositions = (grid: GridStack): DashboardSectionPosition[] => {
  if (!isGridActive(grid)) {
    return [];
  }

  try {
    return grid
      .getGridItems()

      .map((item) => {
        const id = item.dataset.gridId;

        const node = item.gridstackNode;

        if (!isDashboardSectionId(id) || !node) {
          return null;
        }

        return {
          id,

          position: 0,

          x: node.x ?? 0,

          y: node.y ?? 0,

          w: node.w ?? 1,

          h: node.h ?? 1,
        };
      })

      .filter((item): item is DashboardSectionPosition => item !== null)

      .sort((a, b) => a.y - b.y || a.x - b.x)

      .map((item, index) => ({
        ...item,

        position: index + 1,
      }));
  } catch (error) {
    console.error("Unable to read GridStack positions:", error);

    return [];
  }
};

/**
 * Apply top-to-bottom order to React-rendered GridStack items.
 */
const applySectionOrder = (
  grid: GridStack,
  order: DashboardSectionId[],
): void => {
  if (!isGridActive(grid)) {
    return;
  }

  const items = grid.getGridItems();

  const itemsById = new Map<DashboardSectionId, GridItemHTMLElement>();

  items.forEach((item) => {
    const id = item.dataset.gridId;

    if (isDashboardSectionId(id)) {
      itemsById.set(id, item);
    }
  });

  let y = 0;

  const layout = order.reduce<GridStackWidget[]>(
    (widgets, id) => {
      const item = itemsById.get(id);

      if (!item) {
        return widgets;
      }

      const h = item.gridstackNode?.h ?? 1;

      widgets.push({
        id,

        x: 0,

        y,

        w: 1,

        h,

        noResize: true,

        sizeToContent: true,
      });

      y += h;

      return widgets;
    },

    [],
  );

  if (layout.length === DEFAULT_SECTION_ORDER.length) {
    grid.load(layout, false);
  }
};

/* ============================================================
 * GRID SECTION
 * ============================================================
 *
 * Header ALWAYS remains visible.
 *
 * Only card-body collapses.
 *
 * Chevron = collapse
 * Grip = drag
 * ============================================================
 */

const GridSection: FC<GridSectionProps> = ({
  id,
  title,
  children,
  isCollapsed,
  onToggleCollapse,
}): JSX.Element => {
  return (
    <div
      className="grid-stack-item"
      data-grid-id={id}
      data-gs-id={id}
      data-gs-x="0"
      data-gs-w="1"
      data-gs-h="1"
      data-gs-no-resize="true"
      data-gs-size-to-content="true"
    >
      <div className="grid-stack-item-content">
        <section className="card action-dashboard-grid-card">
          {/* ==================================================
              HEADER - ALWAYS VISIBLE
          ================================================== */}

          <div className="card-header custom-bg-purple d-flex align-items-center justify-content-between gap-3 " onClick={(event) => {
            onToggleCollapse();
          }}>
            <h3 className="text-white mb-0 flex-grow-1">{title}</h3>

            {/* =================================================
                HEADER ACTIONS
            ================================================= */}

            <div className="d-flex align-items-center gap-3">
              {/* ===============================================
                  COLLAPSE ARROW

                  ONLY THIS BUTTON COLLAPSES THE BODY
              =============================================== */}

              <button
                type="button"
                className="btn btn-link text-white p-0 border-0 action-dashboard-collapse-button"
                title={isCollapsed ? `Expand ${id}` : `Collapse ${id}`}
                aria-label={
                  isCollapsed
                    ? `Expand ${id} section`
                    : `Collapse ${id} section`
                }
                aria-expanded={!isCollapsed}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}

              >
                <i
                  className={`fas ${isCollapsed ? "fa-chevron-down" : "fa-chevron-up"
                    }`}
                  aria-hidden="true"
                />
              </button>

              {/* ===============================================
                  DRAG HANDLE

                  ONLY THIS MOVES THE SECTION
              =============================================== */}
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  className="action-dashboard-drag-handle"
                  title={`Move ${id}`}
                  aria-label={`Move ${id} section`}
                >
                  <i className="fas fa-grip-vertical" aria-hidden="true" />
                </button>
              </div>

            </div>
          </div>

          {/* ==================================================
              BODY

              ONLY THIS PART COLLAPSES
          ================================================== */}

          {!isCollapsed && <div className="card-body p-0">{children}</div>}
        </section>
      </div>
    </div>
  );
};

/* ============================================================
 * MANUFACTURER ACTION DASHBOARD
 * ============================================================
 */

const ActionDashboardManufactureListing: FC = (): JSX.Element => {
  /* ========================================================
   * REDUCER
   * ========================================================
   */

  const [queryParams, dispatch] = useReducer(tableFilterReducer, initialState);

  const [searchParams, updateSearchParams] = useSearchParams();

  /* ========================================================
   * COLLAPSED SECTIONS
   * ========================================================
   */

  const [collapsedSections, setCollapsedSections] = useState<
    Record<DashboardSectionId, boolean>
  >({
    coverage_available: false,

    action_required: false,

    specialty_pharmacy: false,

    pending: false,
  });

  /* ========================================================
   * GRIDSTACK REFS
   * ========================================================
   */

  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  const gridInstanceRef = useRef<GridStack | null>(null);

  const refreshFrameRef = useRef<number | null>(null);

  const secondRefreshFrameRef = useRef<number | null>(null);

  const currentOrderRef = useRef<DashboardSectionId[]>([
    ...DEFAULT_SECTION_ORDER,
  ]);

  const saveOrderRef = useRef<(order: DashboardSectionId[]) => void>(
    () => undefined,
  );

  const pendingSectionOrderRef = useRef<DashboardSectionId[] | null>(null);

  /* ========================================================
   * CONTEXT
   * ========================================================
   */

  const { setLoader, hideLoader } = useContext(LoaderContext);

  const { userData } = useContext(AuthContext);

  /* ========================================================
   * REQUEST PARAMS
   * ========================================================
   */

  const {
    start_date: _startDate,

    end_date: _endDate,

    ...cleanedQueries
  } = queryParams;

  /* ========================================================
   * DASHBOARD API
   * ========================================================
   */

  const {
    loading: loadingDashboardData,

    data: dashboardData,

    mutate: updateDataWithQueryParams,

    error: todoActionDashboardError,
  } = useFetch<TodoResponseType>(
    APIEndpoints.TODO_ACTION_DASHBOARD_STATISTICS,

    cleanedQueries,

    {},

    false,
  );

  /* ========================================================
   * SECTION ORDER API
   * ========================================================
   */

  const {
    data: sectionOrderUpdateResponse,

    error: sectionOrderUpdateError,

    mutate: updateSectionOrderData,
  } = useFetch<TodoResponseType>(
    APIEndpoints.ACTIONDASHBOARD_SECTION_ORDER,

    {},

    {},

    false,
  );

  /* ========================================================
   * DASHBOARD DATA
   * ========================================================
   */

  const coverageAvailable = dashboardData?.data?.coverage_available;

  const actionRequired = dashboardData?.data?.action_required;

  const specialtyPharmacy = dashboardData?.data?.specialty_pharmacy;

  const pending = dashboardData?.data?.pending;

  /* ========================================================
   * SECTION ORDER
   * ========================================================
   */

  const apiSectionOrder = useMemo(
    () => resolveSectionOrder(dashboardData?.data?.section_order),

    [dashboardData?.data?.section_order],
  );

  /* ========================================================
   * PRACTICE / DOCTOR LISTS
   * ========================================================
   */

  const [practiceListing, setPracticeListing] = useState<
    TodoResponseType["data"]["practice_dropdown"]
  >([]);

  const [doctorListing, setDoctorListing] = useState<
    TodoResponseType["data"]["doctor_dropdown"]
  >([]);

  /* ========================================================
   * SAVE SECTION ORDER
   * ========================================================
   */

  const saveSectionOrder = useCallback(
    (order: DashboardSectionId[]): void => {
      currentOrderRef.current = order;

      pendingSectionOrderRef.current = order;

      updateSectionOrderData({
        section_order: order,
      });
    },

    [updateSectionOrderData],
  );

  /*
   * Keep GridStack callback connected to latest save function.
   */
  useEffect(() => {
    saveOrderRef.current = saveSectionOrder;
  }, [saveSectionOrder]);

  /* ========================================================
   * INITIAL LOADER
   * ========================================================
   */

  useLayoutEffect(() => {
    setLoader();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ========================================================
   * GRID REFRESH
   * ========================================================
   *
   * Same pattern as ActionDashboardListing.
   *
   * Resize content, then preserve current order.
   */

  const scheduleGridRefresh = useCallback(
    (grid: GridStack): void => {
      if (refreshFrameRef.current !== null) {
        window.cancelAnimationFrame(refreshFrameRef.current);

        refreshFrameRef.current = null;
      }

      if (secondRefreshFrameRef.current !== null) {
        window.cancelAnimationFrame(secondRefreshFrameRef.current);

        secondRefreshFrameRef.current = null;
      }

      refreshFrameRef.current = window.requestAnimationFrame(() => {
        refreshFrameRef.current = null;

        secondRefreshFrameRef.current = window.requestAnimationFrame(() => {
          secondRefreshFrameRef.current = null;

          if (gridInstanceRef.current !== grid || !isGridActive(grid)) {
            return;
          }

          /*
           * Measure after React and the browser have restored the body.
           * Reapply the order using those new heights, then measure once
           * more because load() can update the widget geometry.
           */
          grid.getGridItems().forEach((item) => {
            grid.resizeToContent(item);
          });

          applySectionOrder(grid, currentOrderRef.current);

          grid.getGridItems().forEach((item) => {
            grid.resizeToContent(item);
          });

          grid.compact("list");
        });
      });
    },

    [],
  );

  /* ========================================================
   * COLLAPSE / EXPAND
   * ========================================================
   */

  const toggleSectionCollapse = useCallback(
    (id: DashboardSectionId): void => {
      setCollapsedSections((previous) => ({
        ...previous,

        [id]: !previous[id],
      }));
    },

    [],
  );

  /*
   * React changes the DOM first.
   * Then tell GridStack that the card height changed.
   */
  useEffect(() => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    scheduleGridRefresh(grid);
  }, [collapsedSections, scheduleGridRefresh]);

  /* ========================================================
   * INITIALIZE GRIDSTACK
   * ========================================================
   */

  useEffect(() => {
    const container = gridContainerRef.current;

    if (!container || gridInstanceRef.current) {
      return;
    }

    const initializedGrid = GridStack.init(
      {
        column: 1,

        cellHeight: 10,

        margin: 12,

        disableResize: true,

        float: false,

        animate: true,

        sizeToContent: true,

        /*
         * Only this button can start a drag.
         */
        handle: ".action-dashboard-drag-handle",

        placeholderClass: "grid-stack-placeholder",
      },

      container,
    );

    if (!initializedGrid) {
      console.error("GridStack initialization failed");

      return;
    }

    const grid: GridStack = initializedGrid;

    gridInstanceRef.current = grid;

    /*
     * Ensure React-rendered items use our section IDs.
     */
    grid.getGridItems().forEach((item) => {
      const id = item.dataset.gridId;

      if (isDashboardSectionId(id)) {
        grid.update(item, {
          id,

          x: 0,

          w: 1,

          noResize: true,

          sizeToContent: true,
        });
      }
    });

    /*
     * Apply initial/server order.
     */
    applySectionOrder(grid, currentOrderRef.current);

    scheduleGridRefresh(grid);

    /* ======================================================
     * DRAG STOP
     * ======================================================
     */

    const handleDragStop = (
      _event: Event,

      draggedElement: GridItemHTMLElement,
    ): void => {
      if (gridInstanceRef.current !== grid || !isGridActive(grid)) {
        return;
      }

      /*
       * Read visual order after drag.
       */
      const positions = getSectionPositions(grid);

      if (positions.length !== DEFAULT_SECTION_ORDER.length) {
        return;
      }

      const order = positions.map(({ id }) => id);

      currentOrderRef.current = order;

      console.log("Dragged section:", draggedElement.dataset.gridId);

      console.log("Updated section order:", order);

      console.table(positions);

      /*
       * Persist the drag order.
       */
      saveOrderRef.current(order);

      /*
       * Normalize height/position.
       */
      scheduleGridRefresh(grid);
    };

    grid.on("dragstop", handleDragStop);

    /* ======================================================
     * CLEANUP
     * ======================================================
     */

    return () => {
      if (refreshFrameRef.current !== null) {
        window.cancelAnimationFrame(refreshFrameRef.current);

        refreshFrameRef.current = null;
      }

      if (secondRefreshFrameRef.current !== null) {
        window.cancelAnimationFrame(secondRefreshFrameRef.current);

        secondRefreshFrameRef.current = null;
      }

      if (gridInstanceRef.current === grid) {
        gridInstanceRef.current = null;
      }

      if (isGridActive(grid)) {
        grid.off("dragstop");

        grid.destroy(false);
      }
    };
  }, [scheduleGridRefresh]);

  /* ========================================================
   * APPLY API SECTION ORDER
   * ========================================================
   */

  useEffect(() => {
    if (!dashboardData) {
      return;
    }

    const pendingOrder = pendingSectionOrderRef.current;

    const serverOrderKey = apiSectionOrder.join("|");

    const pendingOrderKey = pendingOrder?.join("|");

    /*
     * Ignore stale dashboard section_order while our
     * dragged order is still being saved.
     */
    if (pendingOrder && serverOrderKey !== pendingOrderKey) {
      return;
    }

    currentOrderRef.current = apiSectionOrder;

    if (serverOrderKey === pendingOrderKey) {
      pendingSectionOrderRef.current = null;
    }

    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    applySectionOrder(grid, apiSectionOrder);

    scheduleGridRefresh(grid);
  }, [dashboardData, apiSectionOrder, scheduleGridRefresh]);

  /* ========================================================
   * SECTION ORDER SAVE RESULT
   * ========================================================
   */

  useEffect(() => {
    if (sectionOrderUpdateError) {
      console.error(
        "Unable to update dashboard section order:",
        sectionOrderUpdateError,
      );

      pendingSectionOrderRef.current = null;

      return;
    }

    if (!sectionOrderUpdateResponse) {
      return;
    }

    pendingSectionOrderRef.current = null;
  }, [sectionOrderUpdateError, sectionOrderUpdateResponse]);

  /* ========================================================
   * LOADER
   * ========================================================
   */

  useEffect(() => {
    if (dashboardData || todoActionDashboardError) {
      hideLoader();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData, todoActionDashboardError]);

  /* ========================================================
   * DROPDOWN OPTIONS
   * ========================================================
   */

  useEffect(() => {
    const practices = dashboardData?.data?.practice_dropdown;

    const doctors = dashboardData?.data?.doctor_dropdown;

    if (practices && practices.length > practiceListing.length) {
      setPracticeListing(practices);
    }

    if (doctors && doctors.length > doctorListing.length) {
      setDoctorListing(doctors);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData]);

  /* ========================================================
   * PAGE CHANGE
   * ========================================================
   */

  const changePageNumber = (
    action: ActionStatisticsDashboardActionType,
  ): void => {
    const updatedState = tableFilterReducer(queryParams, action);

    const {
      start_date: _updatedStartDate,

      end_date: _updatedEndDate,

      ...requestParams
    } = updatedState;

    dispatch(action);

    setLoader();

    updateSearchParams(
      new URLSearchParams(requestParams as unknown as Record<string, string>),

      {
        replace: false,
      },
    );

    updateDataWithQueryParams({}, {}, requestParams);
  };

  /* ========================================================
   * MANUFACTURER USER CHANGE
   * ========================================================
   */

  const reTriggerPatientStatsWhenUserChange = (value: UserObjectType): void => {
    const updatedParams = {
      ...initialState,

      role: value.role,

      id: value.id.toString(),

      ref_user: value.role !== "Manufacturer" ? value.id.toString() : "",
    };

    const {
      start_date: _updatedStartDate,

      end_date: _updatedEndDate,

      ...requestParams
    } = updatedParams;

    setLoader();

    setPracticeListing([]);

    setDoctorListing([]);

    updateSearchParams(
      new URLSearchParams({
        id: value.id.toString(),

        role: value.role,
      }),

      {
        replace: false,
      },
    );

    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,

      payload: updatedParams,
    });

    updateDataWithQueryParams({}, {}, requestParams);
  };

  /* ========================================================
   * RESET DASHBOARD LAYOUT
   * ========================================================
   */

  const resetDashboardLayout = (): void => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    const defaultOrder = [...DEFAULT_SECTION_ORDER];

    currentOrderRef.current = defaultOrder;

    applySectionOrder(grid, defaultOrder);

    scheduleGridRefresh(grid);

    saveOrderRef.current(defaultOrder);
  };

  /* ========================================================
   * FILTER
   * ========================================================
   */

  const updateQueryParamsAndCallAPI = (
    updatedQueries: PatientManufacturerFilterStart,

    isReset = false,
  ): void => {
    setLoader();

    /* ======================================================
     * RESET
     * ======================================================
     */

    if (isReset) {
      const role = getRole() || userData?.ref_type || "";

      const id = userData?.id?.toString() || "";

      const defaultQueryParams = tableFilterReducer(queryParams, {
        type: PatientFilterStateActions.RESET_FILTERS,

        payload: {
          role,

          id,

          ref_user: role !== "Manufacturer" ? id : "",
        },
      });

      const {
        start_date: _defaultStartDate,

        end_date: _defaultEndDate,

        ...requestParams
      } = defaultQueryParams;

      setPracticeListing([]);

      setDoctorListing([]);

      updateSearchParams(
        new URLSearchParams({
          role,

          id,

          ref_user: role !== "Manufacturer" ? id : "",
        }),

        {
          replace: false,
        },
      );

      dispatch({
        type: PatientFilterStateActions.RESET_FILTERS,

        payload: defaultQueryParams,
      });

      updateDataWithQueryParams({}, {}, requestParams);

      return;
    }

    /* ======================================================
     * APPLY FILTER
     * ======================================================
     */

    const updatedParams = {
      ...queryParams,

      case_id: updatedQueries.case_id,

      global_doctor_id: updatedQueries.global_doctor_id,

      global_patient_id: updatedQueries.global_patient_id,

      practice_id: updatedQueries.practice_id,

      start_date: updatedQueries.start_date,

      end_date: updatedQueries.end_date,

      treatable_page: 1,

      todo_page: 1,

      sp_treatment_page: 1,
      
      not_treated_page: 1
    };

    const {
      start_date: _updatedStartDate,

      end_date: _updatedEndDate,

      ...requestParams
    } = updatedParams;

    updateSearchParams(
      new URLSearchParams(requestParams as unknown as Record<string, string>),

      {
        replace: false,
      },
    );

    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,

      payload: updatedParams,
    });

    updateDataWithQueryParams({}, {}, requestParams);
  };

  /* ========================================================
   * INITIALIZE FILTERS
   * ========================================================
   */

  useEffect(() => {
    const role =
      searchParams.get("role") || getRole() || userData?.ref_type || "";

    const id = searchParams.get("id") || userData?.id?.toString() || "";

    const updatedParams = {
      ...queryParams,

      case_id: searchParams.get("case_id") || "",

      global_patient_id: searchParams.get("global_patient_id") || "",

      global_doctor_id: searchParams.get("global_doctor_id") || "",

      practice_id: searchParams.get("practice_id") || "",

      role,

      id,

      ref_user: role !== "Manufacturer" ? id : "",
    };

    if (loadingDashboardData) {
      return;
    }

    const {
      start_date: _updatedStartDate,

      end_date: _updatedEndDate,

      ...requestParams
    } = updatedParams;

    setLoader();

    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,

      payload: updatedParams,
    });

    updateDataWithQueryParams({}, {}, requestParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryParams.global_doctor_id,
    queryParams.global_patient_id,
    queryParams.practice_id,
    queryParams.case_id,
  ]);

  /* ========================================================
   * RENDER SECTION
   *
   * Same pattern as ActionDashboardListing.
   * ========================================================
   */

  const renderSection = (id: DashboardSectionId): JSX.Element => {
    switch (id) {
      /* ====================================================
       * COVERAGE AVAILABLE
       * ====================================================
       */

      case "coverage_available":
        return (
          <GridSection
            key={id}
            id={id}
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
            title={
              <>
                Coverage Available - You have{" "}
                {coverageAvailable?.dpn_count ?? 0} new DPN patients over the
                last 9 months in this queue
              </>
            }
          >
            <div className="custom-bg-light-purple p-2">
              <p className="mb-0 text-dark fs-6">
                These are patients that have either medical coverage available
                and/or pharmacy coverage available. Please provide patient
                status update when a treatment has been completed or not. For
                Case Type, 'A' indicates an automatically rerun case, 'M'
                indicates a manually re-run case, and 'New' indicates a case for
                a new patient.
              </p>
            </div>

            <CoverageAvailable
              tableData={coverageAvailable}
              currentPage={queryParams.treatable_page}
              onPageChange={changePageNumber}
              onActionPerformed={() =>
                updateDataWithQueryParams({}, {}, queryParams)
              }
            />
          </GridSection>
        );

      /* ====================================================
       * ACTION REQUIRED
       * ====================================================
       */

      case "action_required":
        return (
          <GridSection
            key={id}
            id={id}
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
            title={
              <>
                Action Required - You have {actionRequired?.dpn_count ?? 0} new
                DPN patients over the last 9 months in this queue
              </>
            }
          >
            <div className="custom-bg-light-purple p-2">
              <p className="mb-0 text-dark fs-6">
                This is a list of all cases requiring your attention before a
                treatment decision should be determined. Click on the
                appropriate next steps to proceed. For Case Type, 'A' indicates
                an automatically rerun case, 'M' indicates a manually re-run
                case, and 'New' indicates a case for a new patient.
              </p>
            </div>

            <ActionRequired
              tableData={actionRequired}
              currentPage={queryParams.todo_page}
              onPageChange={changePageNumber}
              onActionPerformed={() =>
                updateDataWithQueryParams({}, {}, queryParams)
              }
            />
          </GridSection>
        );

      /* ====================================================
       * SPECIALTY PHARMACY
       * ====================================================
       */

      case "specialty_pharmacy":
        return (
          <GridSection
            key={id}
            id={id}
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
            title={
              <>
                Transferred to Specialty Pharmacy - You have{" "}
                {specialtyPharmacy?.dpn_count ?? 0} new DPN patients over the
                last 9 months in this queue
              </>
            }
          >
            <div className="custom-bg-light-purple p-2">
              <p className="mb-0 text-dark fs-6">
                This is a list of patients awaiting specialty pharmacy shipment
                to your office. Please provide patient status update when a
                treatment has been completed or not. For Case Type, 'A'
                indicates an automatically rerun case, 'M' indicates a manually
                re-run case, and 'New' indicates a case for a new patient.
              </p>
            </div>

            <SpecialityPharmacy
              tableData={specialtyPharmacy}
              currentPage={queryParams.sp_treatment_page}
              onPageChange={changePageNumber}
              onActionPerformed={() =>
                updateDataWithQueryParams({}, {}, queryParams)
              }
            />
          </GridSection>
        );

      /* ====================================================
       * PENDING
       * ====================================================
       */

      case "pending":
        return (
          <GridSection
            key={id}
            id={id}
            title="Pending"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <div className="custom-bg-light-purple p-2">
              <p className="mb-0 text-dark fs-6">
                This is a list of cases with no or indeterminate coverage. Click
                on the appropriate patient status to update the case. For Case
                Type, 'A' indicates an automatically rerun case, 'M' indicates a
                manually re-run case, and 'New' indicates a case for a new
                patient.
              </p>
            </div>

            <PendingTable
              tableData={pending}
              currentPage={queryParams.not_treated_page}
              onPageChange={changePageNumber}
              onActionPerformed={() =>
                updateDataWithQueryParams({}, {}, queryParams)
              }
            />
          </GridSection>
        );
    }
  };

  /* ========================================================
   * RENDER
   * ========================================================
   */

  return (
    <div className="container-fluid overflow-auto">
      {/* ====================================================
            HEADER
        ==================================================== */}

      <AuthSubHeader
        defaultUser={{
          id: searchParams.get("id") || "",

          role: searchParams.get("role") || "",
        }}
        reTriggerPatientStats={reTriggerPatientStatsWhenUserChange}
      />

      {/* ====================================================
            FILTERS
        ==================================================== */}

      <div className="card mt-2">
        <div className="card-body">
          <ActionDashBoardFilter
            doctorDropdown={doctorListing}
            practiceDropdown={practiceListing}
            defaultQueryParams={queryParams}
            updateQueryParams={(params, reset) =>
              updateQueryParamsAndCallAPI(
                params as PatientManufacturerFilterStart,
                reset,
              )
            }
          />
        </div>
      </div>

      {/* ====================================================
            RESET SECTION ORDER
        ==================================================== */}

      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm action-dashboard-reset-button me-3"
          onClick={resetDashboardLayout}
        >
          <i className="fas fa-undo-alt me-2" aria-hidden="true" />
          Reset Section Order
        </button>
      </div>

      {/* ====================================================
            GRIDSTACK

            Same rendering pattern as ActionDashboardListing.
        ==================================================== */}

      <div className="d-flex flex-column">
        <div
          ref={gridContainerRef}
          className="grid-stack action-dashboard-grid mt-3"
        >
          {DEFAULT_SECTION_ORDER.map(renderSection)}
        </div>
      </div>

      <FooterText />
    </div>
  );
};

export default ActionDashboardManufactureListing;
